import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import states from '@/data/states.json';
import provinces from '@/data/provinces.json';
import countries from '@/data/countries.json';
import cx from '@/lib/cx';

import FormFieldGroup from './FormFieldGroup';
import FormNote from './FormNote';
import FormSelect from './FormSelect';
import ControlledField from './ControlledField';

const COUNTRY_STATES = {
  US: states,
  CA: provinces,
};

const COUNTRY_STATE_LABELS = {
  US: 'State',
  CA: 'Province',
};

export type AddressField =
  | 'address1'
  | 'address2'
  | 'city'
  | 'stateProv'
  | 'postalCode'
  | 'country';

interface FormAddressFieldsProps {
  label?: string;
  helpText?: string;
  isRequired?: boolean;
  hideLabels?: boolean;
  getFieldName?: (prop: AddressField) => string;
}

const FormAddressFields = ({
  label = 'Address',
  helpText,
  isRequired,
  getFieldName = (x) => x,
  hideLabels,
}: FormAddressFieldsProps) => {
  const { watch, getValues, setValue } = useFormContext();

  const countryFieldName = getFieldName('country');
  const stateFieldName = getFieldName('stateProv');

  const [country, state] = watch([countryFieldName, stateFieldName], {
    [countryFieldName]: getValues(countryFieldName) || 'US',
  });

  const stateList = useMemo(() => {
    const predefined = COUNTRY_STATES[country];

    if (!predefined) return null;
    if (!state) return predefined;

    return predefined.some((x) => x.code === state)
      ? predefined
      : [...predefined, { label: state, code: state }];
  }, [state, country]);

  const countryList = useMemo(() => {
    if (!country) return countries;
    return countries.some((x) => x.code === country)
      ? countries
      : [...countries, { code: country, label: country }];
  }, [country]);

  return (
    <FormFieldGroup>
      <ControlledField name={getFieldName('address1')}>
        <div>
          <ControlledField.Label className={cx({ 'sr-only': hideLabels })} isRequired={isRequired}>
            {label}
          </ControlledField.Label>
          {helpText && <FormNote>{helpText}</FormNote>}
        </div>
        <ControlledField.Control type="text" placeholder="Street address, P.O. box" />
        <ControlledField.ErrorMessage />
      </ControlledField>

      <ControlledField name={getFieldName('address2')}>
        <ControlledField.Label className={cx({ 'sr-only': hideLabels })}>
          Address 2
        </ControlledField.Label>
        <ControlledField.Control type="text" placeholder="Apt., Suite, Bldg, etc." />
        <ControlledField.ErrorMessage />
      </ControlledField>

      <ControlledField name={getFieldName('city')}>
        <ControlledField.Label className={cx({ 'sr-only': hideLabels })} isRequired={isRequired}>
          City
        </ControlledField.Label>
        <ControlledField.Control type="text" placeholder="Beverly Hills" />
        <ControlledField.ErrorMessage />
      </ControlledField>

      <div className="grid grid-cols-2 gap-4 items-start">
        <ControlledField name={stateFieldName}>
          <ControlledField.Label
            className={cx({ 'sr-only': hideLabels })}
            isRequired={isRequired}
            id={`${stateFieldName}:label`}
          >
            {COUNTRY_STATE_LABELS[country] ?? 'Region'}
          </ControlledField.Label>
          {stateList ? (
            <ControlledField.Control
              as={FormSelect}
              placeholder="-"
              aria-labelledby={`${stateFieldName}:label`}
              options={stateList.map((x) => ({ label: x.code, value: x.code }))}
              isSearchable={false}
              isClearable={!isRequired}
            />
          ) : (
            <ControlledField.Control type="text" />
          )}
          <ControlledField.ErrorMessage className="col-start-1" />
        </ControlledField>

        <ControlledField name={getFieldName('postalCode')}>
          <ControlledField.Label className={cx({ 'sr-only': hideLabels })} isRequired={isRequired}>
            Zip
          </ControlledField.Label>
          <ControlledField.Control type="text" placeholder="90210" />
          <ControlledField.ErrorMessage className="col-start-1" />
        </ControlledField>
      </div>

      <ControlledField name={countryFieldName} defaultValue="US">
        <ControlledField.Label
          className={cx({ 'sr-only': hideLabels })}
          isRequired={isRequired}
          id={`${countryFieldName}:label`}
        >
          Country
        </ControlledField.Label>
        <ControlledField.Control
          as={FormSelect}
          options={countryList.map((x) => ({ label: x.label, value: x.code }))}
          isClearable={false}
          aria-labelledby={`${countryFieldName}:label`}
          onChange={(newValue) => {
            if (state && newValue && newValue !== country) {
              setValue(stateFieldName, null);
            }
          }}
        />
        <ControlledField.ErrorMessage />
      </ControlledField>
    </FormFieldGroup>
  );
};

export default FormAddressFields;
